import { useMemo, useState } from 'react'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'
import {
  BORDER,
  RADIUS,
  TrendArrow,
  cardStyle,
  formatChf,
  formatPct,
  kpiLabelStyle,
  kpiValueStyle,
  sectionStyle,
  sectionTitleStyle,
} from './analyseShared'

const SAISON_COLORS = {
  Sport: '#60a5fa',
  Naked: '#a78bfa',
  Trail: '#34d399',
  Roadster: '#fbbf24',
}

export function SectionVueMarche({ data, loading, error }) {
  const [chartMode, setChartMode] = useState('prix')
  const [sort, setSort] = useState({ key: 'volume', dir: 'desc' })

  const saisonData = useMemo(() => {
    if (!data) return []
    const src = chartMode === 'volume' ? data.saisonnalite_volume : data.saisonnalite
    return src?.series || []
  }, [data, chartMode])

  const sortedSegments = useMemo(() => {
    const rows = [...(data?.segments_table || [])]
    const { key, dir } = sort
    rows.sort((a, b) => {
      const av = a[key] ?? -Infinity
      const bv = b[key] ?? -Infinity
      if (av === bv) return 0
      return dir === 'asc' ? (av > bv ? 1 : -1) : av < bv ? 1 : -1
    })
    return rows
  }, [data, sort])

  const toggleSort = (key) => {
    setSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }
    )
  }

  if (loading) return <SectionShell title="1 — Vue marché" state="Chargement…" />
  if (error) return <SectionShell title="1 — Vue marché" state={error} error />
  if (!data) return null

  const kpis = data.kpis || {}

  return (
    <section style={sectionStyle}>
      <h2 style={sectionTitleStyle}>1 — Vue marché</h2>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 16,
          marginBottom: 28,
        }}
      >
        <KpiCard label="Annonces actives" value={kpis.volume_actif?.toLocaleString('fr-CH') ?? '—'} sub={formatPct(kpis.volume_evolution_30j_pct)} />
        <KpiCard label="Prix médian" value={formatChf(kpis.prix_median)} sub={formatPct(kpis.prix_evolution_30j_pct)} />
        <KpiCard label="Délai médian vente" value={kpis.delai_median_jours != null ? `${kpis.delai_median_jours} j` : '—'} sub={formatPct(kpis.delai_evolution_30j_pct)} />
        <KpiCard label="Nouveaux modèles (mois)" value={kpis.nouveaux_modeles_mois ?? '—'} />
      </div>

      <div style={{ overflowX: 'auto', marginBottom: 28 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: BORDER, color: 'var(--text-tertiary)', fontSize: 11, textTransform: 'uppercase' }}>
              {[
                ['segment', 'Segment'],
                ['volume', 'Volume'],
                ['prix_median', 'Prix médian'],
                ['delai_median', 'Délai vente'],
                ['tendance_90j_pct', 'Tendance 90j'],
              ].map(([key, label]) => (
                <th
                  key={key}
                  style={{ textAlign: key === 'segment' ? 'left' : 'right', padding: '10px 12px', cursor: 'pointer' }}
                  onClick={() => toggleSort(key)}
                >
                  {label}
                  {sort.key === key ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedSegments.map((row) => (
              <tr key={row.segment} style={{ borderBottom: BORDER }}>
                <td style={{ padding: '12px', fontWeight: 500 }}>{row.segment}</td>
                <td style={{ padding: '12px', textAlign: 'right' }}>{row.volume}</td>
                <td style={{ padding: '12px', textAlign: 'right' }}>{formatChf(row.prix_median)}</td>
                <td style={{ padding: '12px', textAlign: 'right' }}>{row.delai_median != null ? `${row.delai_median} j` : '—'}</td>
                <td style={{ padding: '12px', textAlign: 'right' }}>
                  <TrendArrow pct={row.tendance_90j_pct} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
          <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Saisonnalité (24 mois)</span>
          <div style={{ display: 'flex', gap: 8 }}>
            {['prix', 'volume'].map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setChartMode(m)}
                style={{
                  padding: '6px 12px',
                  fontSize: 11,
                  borderRadius: 4,
                  border: BORDER,
                  background: chartMode === m ? 'rgba(0,30,80,0.5)' : 'transparent',
                  color: chartMode === m ? '#fff' : 'var(--muted)',
                  cursor: 'pointer',
                }}
              >
                {m === 'prix' ? 'Prix médian' : 'Volume'}
              </button>
            ))}
          </div>
        </div>
        <div style={{ width: '100%', height: 280 }}>
          <ResponsiveContainer>
            <LineChart data={saisonData}>
              <CartesianGrid stroke="rgba(255,255,255,0.04)" vertical={false} />
              <XAxis dataKey="mois" tick={{ fill: 'var(--muted)', fontSize: 10 }} />
              <YAxis tick={{ fill: 'var(--muted)', fontSize: 10 }} />
              <Tooltip
                contentStyle={{ background: '#1a1f2e', border: BORDER, borderRadius: RADIUS }}
                formatter={(v) => (chartMode === 'prix' ? formatChf(v) : v)}
              />
              <Legend />
              {Object.keys(SAISON_COLORS).map((seg) => (
                <Line key={seg} type="monotone" dataKey={seg} stroke={SAISON_COLORS[seg]} strokeWidth={1.5} dot={false} connectNulls />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </section>
  )
}

function KpiCard({ label, value, sub }) {
  return (
    <div style={cardStyle}>
      <div style={kpiLabelStyle}>{label}</div>
      <div style={kpiValueStyle}>{value}</div>
      {sub != null && sub !== '—' && (
        <div style={{ fontSize: 12, marginTop: 8, color: 'var(--text-secondary)' }}>{sub}</div>
      )}
    </div>
  )
}

function SectionShell({ title, state, error }) {
  return (
    <section style={sectionStyle}>
      <h2 style={sectionTitleStyle}>{title}</h2>
      <p style={{ color: error ? '#fca5a5' : 'var(--muted)', fontSize: 13 }}>{state}</p>
    </section>
  )
}